import React from "react";

import { cn } from "../../utils/cn";

interface WidgetShellProps {
    children: React.ReactNode;
    button: React.ReactNode;
    footer?: React.ReactNode;
    /** Whether the footer is expanded */
    footerExpanded?: boolean;
    cardRef?:
        | React.RefObject<HTMLDivElement | null>
        | ((node: HTMLDivElement | null) => void);
    cardStyle?: React.CSSProperties;
    /** Merged onto the shell's outer wrapper. The shell is `w-full`
     *  by default and inherits width from its parent column — pass a
     *  `max-w-*` here only when a specific consumer needs to override
     *  that parent (rare). tailwind-merge resolves any class
     *  conflicts so the passed class wins. */
    className?: string;
}

/**
 * Outer shell for the trade widget card: a bordered card with an ember accent
 * line, no tabs, responsive padding, and an animated footer slot that slides
 * in/out. Used on the USDv landing page.
 *
 * It used to carry a second `"swap"` variant — a raised card with Swap/Stake
 * panel tabs and Buy/Sell/Stake/Unstake/Withdraw sub-tabs — for the USDv
 * positions page. That page is gone; `BuyWidget` is the only caller left and
 * it asks for the landing shape, so the variant, its tab tables and the
 * `panelTab` / `inlineTab` props that fed them are removed rather than kept
 * as a shape nothing can select.
 *
 * @version 3.0.0
 */
export function WidgetShell({
    children,
    button,
    footer,
    footerExpanded = false,
    cardRef,
    cardStyle,
    className
}: WidgetShellProps): React.ReactElement {
    return (
        <div className={cn("w-full relative flex flex-col gap-2", className)}>
            {/* Ember accent line — full width, top */}
            <div className="absolute top-0 left-0 right-0 h-px md:h-[2px] bg-accent" />

            {/* Card */}
            <div
                ref={cardRef}
                className="border md:border-2 border-line-base bg-surface-ground px-6 py-4 md:py-6 flex flex-col"
                style={cardStyle}
            >
                {/* Inputs stack — tighter spacing between You Send / Rate / You Get.
                    `flex-1` so it absorbs any extra vertical space the card-height
                    lock leaves at form/pending heights — pins the button + footer
                    to the bottom of the card instead of letting them float up
                    directly below the inputs with empty space underneath. */}
                <div className="flex flex-col gap-2 md:gap-6 flex-1">
                    {children}
                </div>

                {/* Button — bumped margin above */}
                <div className="mt-6">{button}</div>

                {/* Footer — slides in below button when expanded.
                    Plain `max-height` transition instead of base-ui
                    Collapsible because Collapsible measures the panel
                    async via `--collapsible-panel-height`, and the
                    variable isn't ready on the *very first* open →
                    the transition snaps the first time, then works
                    on subsequent opens. A static maxHeight (300px,
                    plenty for 3 FeeFooter rows) gives a stable
                    from/to pair on every open. Expanded margin
                    (`mt-4 md:mt-6`) lives on the className so the
                    responsive 16/24px split is express; inline style
                    keeps the opacity + transition wiring (margin-top
                    is in the transition list so the className change
                    animates). */}
                {footer && (
                    <div
                        className={`overflow-hidden ${footerExpanded ? "mt-4 md:mt-6" : "mt-0"}`}
                        style={{
                            maxHeight: footerExpanded ? 300 : 0,
                            opacity: footerExpanded ? 1 : 0,
                            transition:
                                "max-height 400ms ease-out, opacity 300ms ease-out 100ms, margin-top 400ms ease-out"
                        }}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
